import { useCallback, useRef, useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import globalState from '@/state/globalState';

const isLocal = (uri: string | null) => /^(file:|content:|data:|blob:)/i.test(uri ?? '');

/** Conserva apenas a prévia local da mesma mídia enquanto a URL remota carrega. */
export const useRetainedLocalMediaPreview = (uri: string | null, scopeKey: string) => {
  const identity = useSelector(() => JSON.stringify([
    globalState.user.user.get()?.id, globalState.user.profile.get()?.empresa_id,
    globalState.user.isAuthenticated.get(), scopeKey,
  ]));
  const [retryVersion, setRetryVersion] = useState(0);
  const current = useRef({ uri, identity, retryVersion, generation: 0, fallback: null as string | null, fallbackGeneration: 0, visible: uri, visibleGeneration: 0 });
  const [loadedGeneration, setLoadedGeneration] = useState<number | null>(null);
  const [failedGeneration, setFailedGeneration] = useState<number | null>(null);
  if (current.current.uri !== uri || current.current.identity !== identity || current.current.retryVersion !== retryVersion) {
    const previous = current.current;
    current.current = {
      uri, identity, retryVersion, generation: previous.generation + 1,
      fallback: previous.identity === identity && /^https?:/i.test(uri ?? '') && isLocal(previous.visible)
        ? previous.visible : null,
      fallbackGeneration: previous.visibleGeneration,
      visible: uri, visibleGeneration: previous.generation + 1,
    };
  }
  const { generation, fallback } = current.current;
  const pending = Boolean(fallback && loadedGeneration !== generation);
  const visibleUri = pending ? fallback : uri;
  current.current.visible = visibleUri;
  current.current.visibleGeneration = pending ? current.current.fallbackGeneration : generation;
  const isCurrent = useCallback(() => current.current.generation === generation && current.current.uri === uri &&
    current.current.identity === identity, [generation, uri, identity]);
  const onLoad = useCallback(() => {
    if (!isCurrent()) return false;
    setFailedGeneration(null);
    setLoadedGeneration(generation);
    return true;
  }, [generation, isCurrent]);
  const onError = useCallback(() => {
    if (!isCurrent()) return false;
    setFailedGeneration(generation);
    return true;
  }, [generation, isCurrent]);
  const retry = useCallback(() => { if (isCurrent()) setRetryVersion(version => version + 1); }, [isCurrent]);
  return { visibleUri, generation, visibleGeneration: current.current.visibleGeneration, pendingUri: pending ? uri : null, pending,
    failed: failedGeneration === generation, onLoad, onError, isCurrent, retry };
};
